/**
 * Billed pieces CSV: every piece the export returned (the period, with the
 * on-screen search and client filter), one row each, newest first.
 *
 * Headers stay English, like the 3PL usage and Consumption CSVs, so
 * spreadsheet formulas keep working whatever language the screen is in.
 * Times are the viewer's local time in `CSV_DATE_TIME_FORMAT`, so the file
 * sorts as text. A piece's client is its name; the org's own pieces carry the
 * org's own name (a file is read away from this screen, where "Your own" means
 * nothing), and a client whose name cannot be read is "Former client".
 *
 * CRLF, no BOM: `downloadCsvFile` owns the BOM (lib/download.ts).
 */
import { format } from 'date-fns';
import { toCsvContent } from '@/lib/csv';
import { CSV_DATE_TIME_FORMAT, FILE_DATE_FORMAT, parseDateValue } from '@/lib/format-dates';
import type { BilledPiece, BillPeriod } from './billing-view';

export const BILLED_PIECES_CSV_HEADERS = [
  'Batched',
  'Order',
  'Product',
  'Variant',
  'SKU',
  'Client',
  'Floor',
] as const;

export interface BilledPiecesCsvContext {
  /** The org's own name, for its own pieces. */
  ownName: string;
}

function csvTime(iso: string): string {
  const date = parseDateValue(iso);
  return date ? format(date, CSV_DATE_TIME_FORMAT) : '';
}

export function pieceClientName(piece: BilledPiece, ownName: string): string {
  if (piece.clientName) return piece.clientName;
  return piece.isOwnPiece ? ownName : 'Former client';
}

export function buildBilledPiecesCsv(rows: readonly BilledPiece[], context: BilledPiecesCsvContext): string {
  const body = rows.map((piece) => [
    csvTime(piece.createdAt),
    piece.orderName ?? '',
    piece.productName ?? '',
    piece.variantName ?? '',
    piece.shopifySku ?? '',
    pieceClientName(piece, context.ownName),
    piece.locationName ?? '',
  ]);
  return toCsvContent([[...BILLED_PIECES_CSV_HEADERS], ...body], { eol: '\r\n' });
}

/** "pieces-2026-09-30-to-2026-10-30.csv": the period's two ends, as its label reads them. */
export function billedPiecesExportFilename(period: BillPeriod): string {
  const day = (iso: string) => {
    const date = parseDateValue(iso);
    return date ? format(date, FILE_DATE_FORMAT) : 'unknown';
  };
  return `pieces-${day(period.start)}-to-${day(period.end)}.csv`;
}
